import {
  Box,
  Typography,
  Divider,
} from "@mui/material";
import React, { useContext, useRef, useState } from "react";
import TreffasButton from "src/components/TreffasButton";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import GoogleRegistration from "../google";
import LinkedInRegistration from "../linkedin";
import BasicInfo from "./BasicInfo";
import http from "src/utils/http";
import Verification from "./Verification";
import { useTranslation } from "react-i18next";
import { Users } from "src/context";
import { useSnackbarContext } from "src/context/SnackbarContext";
import { useNavigate } from "react-router-dom";
import Timezone from "src/utils/Timezone";
import momentTimezone from "moment-timezone";

const TreffasRegistration = () => {
  const [isProcess, setIsProcess] = useState();
  const submitBtnRef = useRef();
  const { showSnackbar } = useSnackbarContext();
  const [terms, setTerms] = useState(false);
  const [category, setCategory] = useState("basic-info"); // basic-info, verification
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { mixpanel } = useContext(Users);
  const [verified, setVerified] = useState({
    enable: false,
    isProcess: false,
  });

  let zone = momentTimezone.tz.guess();

  const Schema = Yup.object({
    firstname: Yup.string().required(
      t("registration.form.validation.required.firstname")
    ),
    lastname: Yup.string().required(
      t("registration.form.validation.required.lastname")
    ),
    password: Yup.string()
      .required(t("registration.form.validation.required.password"))
      .min(6, t("registration.form.validation.invalid.password-short")),
    email: Yup.string()
      .email(t("registration.form.validation.invalid.email"))
      .required(t("registration.form.validation.required.email")),
  }).required();

  const {
    handleSubmit,
    control,
    trigger,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(Schema),
    defaultValues: {
      firstname: "",
      lastname: "",
      password: "",
      currency: "USD",
      email: "",
      timezone: Timezone.getLabel(zone),
      timezone_utc: zone,
    },
  });

  const onSubmit = (values) => {
    setIsProcess(true);
    http
      .post("/api/oauth/registration", {
        ...values,
      })
      .then(({ data }) => {
        console.log(`data:`, data);

        if (data.message === "email-exist") {
          showSnackbar(
            t("registration.form.validation.invalid.email-not-available"),
            "error"
          );
        }

        if (data.message === "invalid-email-format") {
          showSnackbar(
            "Invalid email format.",
            "error"
          );
        }

        if (data.message === "email-domain-not-exist") {
          showSnackbar(
            "Email domain does not exist.",
            "error"
          );
        }

        if (data.message === "unsuccess") {
          if (data.login_message === "account-unverified") {
            setVerified((prev) => ({
              ...prev,
              enable: true,
            }));
            showSnackbar("Email already exist but not verified.", "error");
          }

          if (data.login_message === "organization-not-found") {
            showSnackbar("Email already exist but no organization.", "error");
          }

          if (data.login_message === "account-invalid") {
            showSnackbar(
              "Email already exist but password is incorrect.",
              "error"
            );
          }

          if (data.login_message === "success") {
            showSnackbar(
              "You're already registered. Signing you in...",
              "success"
            );

            setTimeout(() => {
              if (data.login_response.two_factor_enable) {
                navigate(
                  `/two-factor/verification?token=${data.login_response.access_token}&id=${data.login_response?.data?.id}&organization=${data.login_response.data.organization.id}&mask=${data.login_response.mask}&type=${data.login_response.two_factor_type}`
                );
              } else {
                localStorage.setItem(
                  `accessToken`,
                  data.login_response.access_token
                );
                localStorage.setItem(`__loginType`, "coach");
                window.location.href = `/app/v2/calendar`;
              }
            }, 3000);
          }
        }

        if (data.message === "success") {
          setCategory("verification");
        }
      })
      .catch((err) => {
        console.log(`error:`, err.message);
      })
      .finally(() => setIsProcess(false));
  };

  const handleResentVerification = () => {
    let email = getValues("email");
    setVerified((prev) => ({
      ...prev,
      isProcess: true,
    }));
    http
      .post("/api/oauth/registration/sent-verification-email", {
        email: email,
        name: null,
        base_url: window.location.origin,
      })
      .then(({ data }) => {
        if (data.message === "success") {
          showSnackbar(
            t("registration.form.validation.verification-sent"),
            "success"
          );
        }
      })
      .catch((err) => {
        console.log(`error:`, err.message);
      })
      .finally(() =>
        setVerified((prev) => ({
          ...prev,
          isProcess: false,
        }))
      );
  };

  React.useEffect(() => {
    mixpanel.track("Registration Page", {
      location: window.location.href,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {category === "basic-info" && (
        <Box
          sx={(themes) => ({
            display: "flex",
            justifyContent: "center",
            [themes.breakpoints.up("md")]: {
              alignItems: "center",
            },
          })}
        >
          <Box
            sx={(themes) => ({
              width: "720px",
              [themes.breakpoints.down("md")]: {
                px: "24px",
                width: "100%",
              },
            })}
          >
            <Box>
              <Typography
                sx={{
                  textAlign: "center",
                  letterSpacing: "-0.01em",
                  color: "#060A2D",
                  fontFamily: "var(--font-grenettepromedium)",
                  fontSize: "26px",
                  lineHeight: "normal",
                }}
              >
                {t("registration.create-account")}
              </Typography>

              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "24px",
                  textAlign: "center",
                  color: "var(--color-gray-700)",
                }}
              >
                {t("registration.description.first")}
              </Typography>
            </Box>

            {/* button */}
            <Box
              sx={{
                mt: "36px",
                display: "flex",
                flexDirection: { md: "row", xs: "column" },
                justifyContent: "center",
                alignItems: "center",
                padding: "0px",
                gap: "20px",
              }}
            >
              <GoogleRegistration />

              <LinkedInRegistration />
            </Box>

            {/* divider */}
            <Box
              sx={(themes) => ({
                [themes.breakpoints.up("md")]: {
                  mt: "34px",
                  mb: "20px",
                },
                [themes.breakpoints.down("md")]: {
                  mt: "40px",
                  mb: "40px",
                },
              })}
            >
              <Box
                sx={{
                  maxWidth: "440px",
                  margin: "auto",
                }}
              >
                <Divider>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "24px",
                      textAlign: "center",
                      color: "var(--color-gray-700)",
                    }}
                  >
                    {t("registration.signup-with-email")}
                  </Typography>
                </Divider>
              </Box>
            </Box>

            <Box
              sx={(themes) => ({
                [themes.breakpoints.up("md")]: {
                  width: "440px",
                  maxWidth: "440px",
                },
                [themes.breakpoints.down("md")]: {
                  width: "100%",
                },
                mx: "auto",
              })}
            >
              {verified.enable && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: "10px",
                  }}
                >
                  <TreffasButton
                    onClick={handleResentVerification}
                    variant="primary"
                    size="sm"
                    loading={verified.isProcess}
                  >
                    Resent verification
                  </TreffasButton>

                  <Box sx={{ mt: "24px" }}>
                    <Divider />
                  </Box>
                </Box>
              )}

              <BasicInfo
                errors={errors}
                control={control}
                isProcess={isProcess}
                setCategory={setCategory}
                trigger={trigger}
                terms={terms}
                setTerms={setTerms}
                submitBtnRef={submitBtnRef}
              />
            </Box>
          </Box>
        </Box>
      )}

      {category === "verification" && (
        <>
          <Verification
            email={getValues("email")}
            name={`${getValues("firstname")} ${getValues("lastname")}`}
          />
        </>
      )}

      <button ref={submitBtnRef} type="submit" hidden>
        Submit
      </button>
    </form>
  );
};

export default TreffasRegistration;
